/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import { useCallback, useEffect, useState } from 'react';
import { ENDPOINTS, restClient } from '../../../../../../interface/api';
import { WaiverListResponse, WaiversListState } from './waiversListTypes';
import { toListQuery } from './waiversListUrl';

export interface WaiversListResult {
  data: WaiverListResponse | null;
  loading: boolean;
  /** Set when the request failed, including when IQ Server could not answer. Never an empty list. */
  error: string | null;
  reload: () => void;
}

const IQ_FAILURE: Record<string, string> = {
  IQ_UNREACHABLE: 'IQ Server could not be reached, so the waivers cannot be shown.',
  IQ_ERROR: 'IQ Server returned an error, so the waivers cannot be shown.',
  IQ_NOT_CONFIGURED: 'IQ Server is not configured.',
  IQ_VERSION_NOT_SUPPORTED: 'The connected IQ Server version does not support this page.',
};

export function waiversErrorMessage(error: unknown, fallback = 'The waivers could not be loaded.'): string {
  const body = (error as { response?: { data?: { reason?: string } } })?.response?.data;
  return (body?.reason && IQ_FAILURE[body.reason]) || fallback;
}

export function useWaiversList(state: WaiversListState): WaiversListResult {
  // A response is only shown for the tab and sub-tab it was read for. Filter, sort and page changes keep the previous
  // rows while loading, but switching tabs must never show the other list's rows.
  const [loaded, setLoaded] = useState<{ source: string; response: WaiverListResponse } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const query = toListQuery(state);
  const source = `${state.type}/${state.view}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    restClient
      .get<WaiverListResponse>(`${ENDPOINTS.FIREWALL_WAIVERS}?${query}`)
      .then((response) => {
        if (!cancelled) {
          setLoaded({ source, response });
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setLoaded(null);
          setError(waiversErrorMessage(e));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [query, source, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const data = loaded?.source === source ? loaded.response : null;
  return { data, loading, error, reload };
}
